/**
 * RecipeBuilder — create or view a recipe (saved_meal with kind='recipe').
 *
 * Ingredients are entered as direct-macro items (kcal + protein/carbs/fat/fiber).
 * Servings count divides totals when the recipe is logged (per-serving math on the server).
 * ED-safe: neutral copy, no "macros burned" or deficit framing.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.js';
import { SAVED_MEALS_KEY } from '../hooks/useSavedMeals.js';

interface Ingredient {
  name: string;
  kcal: string;
  proteinG: string;
  carbsG: string;
  fatG: string;
  fiberG: string;
}

interface Props {
  onClose: () => void;
  onSaved?: () => void;
}

const EMPTY_INGREDIENT: Ingredient = {
  name: '',
  kcal: '',
  proteinG: '',
  carbsG: '',
  fatG: '',
  fiberG: '',
};

function parseMacro(v: string): number {
  const n = Number.parseFloat(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function sumMacros(ingredients: Ingredient[]) {
  return ingredients.reduce(
    (acc, ing) => ({
      kcal: acc.kcal + parseMacro(ing.kcal),
      proteinG: acc.proteinG + parseMacro(ing.proteinG),
      carbsG: acc.carbsG + parseMacro(ing.carbsG),
      fatG: acc.fatG + parseMacro(ing.fatG),
      fiberG: acc.fiberG + parseMacro(ing.fiberG),
    }),
    { kcal: 0, proteinG: 0, carbsG: 0, fatG: 0, fiberG: 0 },
  );
}

export function RecipeBuilder({ onClose, onSaved }: Props) {
  const { t } = useTranslation('settings');
  const qc = useQueryClient();

  const [recipeName, setRecipeName] = useState('');
  const [servings, setServings] = useState('1');
  const [ingredients, setIngredients] = useState<Ingredient[]>([{ ...EMPTY_INGREDIENT }]);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async (body: {
      name: string;
      kind: 'meal' | 'recipe';
      servings: number;
      items?: {
        name: string;
        kcal: number;
        proteinG: number;
        carbsG: number;
        fatG: number;
        fiberG: number;
      }[];
    }) => api.POST('/me/saved-meals', { body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: SAVED_MEALS_KEY }),
  });

  const isBusy = create.isPending;

  function setIngredientField(idx: number, field: keyof Ingredient, val: string) {
    setIngredients((prev) => {
      const next = [...prev];
      // biome-ignore lint/style/noNonNullAssertion: idx is always in range
      next[idx] = { ...next[idx]!, [field]: val };
      return next;
    });
  }

  function addIngredient() {
    setIngredients((prev) => [...prev, { ...EMPTY_INGREDIENT }]);
  }

  function removeIngredient(idx: number) {
    setIngredients((prev) => prev.filter((_, i) => i !== idx));
  }

  const totals = sumMacros(ingredients);
  const sv = Math.max(1, Number.parseInt(servings, 10) || 1);
  const perServing = {
    kcal: Math.round((totals.kcal / sv) * 10) / 10,
    proteinG: Math.round((totals.proteinG / sv) * 10) / 10,
    carbsG: Math.round((totals.carbsG / sv) * 10) / 10,
    fatG: Math.round((totals.fatG / sv) * 10) / 10,
    fiberG: Math.round((totals.fiberG / sv) * 10) / 10,
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!recipeName.trim()) {
      setError(t('recipe.nameRequired'));
      return;
    }
    const validIngredients = ingredients.filter((ing) => ing.name.trim());
    if (validIngredients.length === 0) {
      setError(t('recipe.ingredientsRequired'));
      return;
    }

    const { error: apiError } = await create.mutateAsync({
      name: recipeName.trim(),
      kind: 'recipe',
      servings: sv,
      items: validIngredients.map((ing) => ({
        name: ing.name.trim(),
        kcal: parseMacro(ing.kcal),
        proteinG: parseMacro(ing.proteinG),
        carbsG: parseMacro(ing.carbsG),
        fatG: parseMacro(ing.fatG),
        fiberG: parseMacro(ing.fiberG),
      })),
    });

    if (apiError) {
      setError(t('recipe.saveFailed'));
      return;
    }

    onSaved?.();
    onClose();
  }

  function handleBackdropKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') onClose();
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop overlay dismisses on click/Escape
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      role="presentation"
      onClick={onClose}
      onKeyDown={handleBackdropKey}
    >
      <div
        className="w-full max-w-lg rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl dark:bg-stone-900 max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rb-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <h2 id="rb-title" className="mb-4 text-lg font-semibold text-ink dark:text-stone-100">
          {t('recipe.createTitle')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Recipe name + servings */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="rb-name" className="mb-1 block text-xs font-medium text-ink-soft">
                {t('recipe.nameLabel')}
              </label>
              <input
                id="rb-name"
                type="text"
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                className="w-full rounded-lg border border-husk px-3 py-2 text-sm dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100"
                placeholder={t('recipe.namePlaceholder')}
                maxLength={120}
              />
            </div>
            <div className="w-24">
              <label htmlFor="rb-servings" className="mb-1 block text-xs font-medium text-ink-soft">
                {t('recipe.servingsLabel')}
              </label>
              <input
                id="rb-servings"
                type="number"
                inputMode="numeric"
                value={servings}
                onChange={(e) => setServings(e.target.value)}
                className="w-full rounded-lg border border-husk px-3 py-2 text-sm dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100"
                min={1}
                max={99}
              />
            </div>
          </div>

          {/* Ingredients */}
          <div>
            <p className="mb-2 text-xs font-medium text-ink-soft">{t('recipe.ingredientsLabel')}</p>
            <ul className="space-y-2">
              {ingredients.map((ing, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: ingredient list has no stable id
                <li key={idx} className="rounded-xl border border-husk p-3 dark:border-stone-700">
                  <div className="mb-2 flex items-center gap-2">
                    <input
                      type="text"
                      value={ing.name}
                      onChange={(e) => setIngredientField(idx, 'name', e.target.value)}
                      className="flex-1 rounded-lg border border-husk px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100"
                      placeholder={t('recipe.ingredientName')}
                      aria-label={t('recipe.ingredientName')}
                    />
                    {ingredients.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeIngredient(idx)}
                        className="text-xs text-red-400 hover:text-red-600"
                        aria-label={t('recipe.removeIngredient')}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-5 gap-1">
                    {(
                      [
                        ['kcal', 'kcal'],
                        ['proteinG', 'P'],
                        ['carbsG', 'C'],
                        ['fatG', 'F'],
                        ['fiberG', 'Fi'],
                      ] as [keyof Ingredient, string][]
                    ).map(([field, label]) => (
                      <div key={field}>
                        <span className="block text-center text-[10px] text-ink-soft">{label}</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          value={ing[field]}
                          onChange={(e) => setIngredientField(idx, field, e.target.value)}
                          className="w-full rounded border border-husk px-1 py-0.5 text-center text-xs dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100"
                          min={0}
                          placeholder="0"
                          aria-label={`${label} ${t('recipe.ingredientName')}`}
                        />
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={addIngredient}
              className="mt-2 text-xs text-leaf hover:underline"
            >
              + {t('recipe.addIngredient')}
            </button>
          </div>

          {/* Per-serving preview */}
          {sv > 1 && (
            <div className="rounded-xl bg-leaf/5 px-4 py-3">
              <p className="mb-1 text-xs font-medium text-leaf">
                {t('recipe.perServingPreview', { count: sv })}
              </p>
              <p className="text-xs text-ink-soft">
                {perServing.kcal} kcal · {perServing.proteinG}g {t('customFood.proteinG')} ·{' '}
                {perServing.carbsG}g {t('customFood.carbsG')} · {perServing.fatG}g{' '}
                {t('customFood.fatG')} · {perServing.fiberG}g {t('customFood.fiberG')}
              </p>
            </div>
          )}

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm text-ink-soft hover:bg-stone-100 dark:hover:bg-stone-800"
            >
              {t('customFood.cancel')}
            </button>
            <button
              type="submit"
              disabled={isBusy}
              className="rounded-lg bg-leaf px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {isBusy ? t('customFood.saving') : t('recipe.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
